
import Signal          from 'fn/signal.js';
import Data            from 'fn/data.js';
import choose          from 'fn/choose.js';
import get             from 'fn/get.js';
import matches         from 'fn/matches.js';
import noop            from 'fn/noop.js';
import overload        from 'fn/overload.js';
import PromiseThrottle from 'fn/promise-throttle.js';
import events          from 'dom/events.js';
import preventDefault  from 'dom/prevent-default.js';
import { requestPatch, requestGet, requestPost, requestDelete, createOptions } from 'dom/request.js'

// WHY??
import { retrieve } from '../modules/resource.js';
import products, { requestCartedFonts } from './products.js';

import { showMessage } from '../../messages/module.js';

const assign = Object.assign;


/* Item */

function inputItem(data) {
    return {
        product: products.find(matches({
            pk:   data.object_pk,
            type: data.object_type
        })),
        quantity: data.quantity
    };
}

function itemFromProduct(product, quantity) {
    return {
        product:     product,
        quantity:    quantity
    };
}

function outputItem(item) {
    return {
        object_pk:     item.product.pk,
        object_type:   item.product.type,
        quantity:      item.quantity
    };
}


/* Cart */

function inputCart(data) {
    return {
        items:         data.content.map(inputItem),
        owner:         data.owner,
        billing:       data.billing,
        discount:      data.discount,
        discount_code: data.discount_code,
        total:         data.total,
        vat:           data.vat,
        vat_required:  data.vat_required,
        message:       data.message,
        contractor:    data.contractor,
        student:       data.student
    };
}

function outputCart(cart) {
    // TODO: Filter out dependent products from items
    return {
        content:       cart.items && cart.items.map(outputItem),
        owner:         cart.owner,
        billing:       cart.billing,
        discount_code: cart.discount_code,
        contractor:    cart.quantity === 1 ?
            false :
            cart.contractor
    };
}

export function requestCart() {
    return Promise.all([
        // Update cart items that are directly in the cart
        retrieve('/api/v1/cart/?carted=1').catch(showMessage),

        // Update products with info about products in the cart
        requestCartedFonts()
    ])
    //.then((responses) => (console.log('GET cart response', responses[0]), responses))
    .then((results) => inputCart(results[0]));
}

export const requestPatchCart = PromiseThrottle(function requestPatchCart(cart) {
    const options = createOptions('PATCH', outputCart(cart), 'application/json');

    fetch('/api/v1/cart/', options)
    .then((response) => {
        if (response.ok) return response.json();

        if (response.status === 400) {
            return response
            .json()
            .then((errors) => {
                throw new Error(Object.values(errors).join(' '));
            });
        }

        throw new Error(response.statusText + '');
    })
    .then(inputCart)
    .then((data) => {
        assign(Data.of(cart), data);
        if (data.message) showMessage({text: data.message, duration: 8, type: 'success'});
        return data;
    })
    .catch(showMessage);
});

function getStateCut(items, product) {
        // Is parent pack in cart ?
    return items
        .filter((item) => item.product.type === 'pack')
        .find((item) => product.pack_pks.includes(item.product.pk)) ? 2 :
        // Its not in items
        0 ;
}

function getStatePack(items, product) {
        // Its in a variable this is in items
    return items.find((item) => item.product.type === 'variable' && product.variable_pks.includes(item.product.pk)) ? 2 :
        // Its a pack and its not in items
        product.type === 'pack' ? 0 :
        // Its not a pack
        getStateCut(items, product) ;
}

function getStateVariable(items, product) {
        // Its in a font that is in items
    return items.find((item) => item.product.type === 'font' && product.font_pk === item.product.pk) ? 2 :
        // Its a variable and its not in items
        product.type === 'variable' ? 0 :
        // Its not a variable
        getStatePack(items, product) ;
}

function getStateFont(items, product) {
        // Product is in items
    return items.find((item) => item.product.type === product.type && item.product.pk === product.pk) ? 1 :
        // Product is a font and not in items
        product.type === 'font' ? 0 :
        // Product is not a font and not in items
        getStateVariable(items, product) ;
}

function getStateCustom(items, product) {
    // Is product in cart ?
    return items.find(match({ product })) ? 1 :
        0 ;
}

function removeProductFromItems(items, product) {
    if(!product) return;
    var i = items.findIndex((item) => item.product.type === product.type && item.product.pk === product.pk);
    if (i < 0) return;
    Data.of(items).splice(i, 1);
}



/* Cart() */

class Cart {
    items     = [];
    owner     = null;
    billing   = null;
    discount  = 0;
    vat       = 0;
    total     = 0;
    discount_code;
    vat_required;

    #contractor = false;
    #quantity = 25;

    constructor() {
        // Request cart data
        requestCart().then((data) => {
            assign(Data.of(this), data);
            if(this.items.length) {
                this.#quantity = this.items[0].quantity;
            }
        });
    }

    add(product, quantity) {
        var n, object;

        if (!product) return;

        // Ignore products already in cart
        if (this.getProductState(product) !== 0) return;

        Data.of(this.items).push(itemFromProduct(product, quantity));

        // If it's a font, remove children
        if (product.type === 'font') {
            n = this.items.length;

            while (n--) {
                object = this.items[n];
                if (object.font_pk === product.pk && object.product !== product) {
                    this.items.splice(n, 1);
                }
            }
        }
        // If it's a variable, remove children
        else if (product.type === 'variable') {
            products
            .filter(function(object) {
                return (object.type === 'pack' || object.type === 'cut')
                    && object.variable_pks
                    && object.variable_pks.indexOf(product.pk) > -1;
            })
            .forEach((product) => removeProductFromItems(this.items, product));
        }
        // If it's a pack, remove children
        else if (product.type === 'pack') {
            products
            .filter(function(object) {
                return object.type === 'cut'
                    && object.pack_pks
                    && object.pack_pks.indexOf(product.pk) > -1;
            })
            .forEach((product) => removeProductFromItems(this.items, product));
        }

        requestPatchCart(this);
        return this;
    }

    remove(product) {
        if (!product) return;
        removeProductFromItems(this.items, product);
        requestPatchCart(this);
    }

    toggleProduct = overload(function(product) { return this.getProductState(product); }, {
        0: function on(product)  { return this.add(product, cart.quantity); },
        1: function off(product) { return this.remove(product); },
        default: noop
    });

    getProductState(product) {
        // TODO: make Data respond to array mutations... not the best indicator of
        // product state I dont think ... it'll do for just now
        Data.of(this.items).length;
        return product.type === 'custom' ?
            getStateCustom(this.items, product) :
            getStateFont(this.items, product) ;
    }

    /* .quantity */
    get quantity() {
        /* @stephband: we now have a quantity property on the cart directly,
        no need to dig into the items. Re Issue #101 */
        const data = Data.of(this).items;
        return data[0] ?
            // All items should have the same quantity
            data[0].quantity :
            // If there are no items
            this.#quantity ;
    }

    set quantity(quantity) {
        // Cache quantity
        this.#quantity = quantity;
        // Changes to `.quantity` are reflected on all items
        this.items.forEach((item) => Data.of(item).quantity = quantity);

        if (this.items.length) requestPatchCart(this);
    }

    /* .contractor */
    get contractor() {
        return this.#contractor;
    }

    set contractor(boolean) {
        if (this.#contractor === !!boolean) return;

        // Cache quantity
        this.#contractor = boolean;
        requestPatchCart(this);
    }
}

const cart = new Cart();
export default Data.of(cart);
